const WISHLIST_STORAGE_KEY = 'dogsee_wishlist_handles';

/** Same path as assets/icon-heart-outline.svg — matches Shop collection cards */
const WISHLIST_HEART_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M10 16.5s-5.5-3.1-5.5-7c0-1.7 1.4-3.1 3.1-3.1.9 0 1.7.4 2.4 1 .7-.6 1.5-1 2.4-1 1.7 0 3.1 1.4 3.1 3.1 0 3.9-5.5 7-5.5 7Z"/></svg>';

function getWishlistHandles() {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => typeof item === 'string' && item.length > 0);
  } catch {
    return [];
  }
}

function setWishlistHandles(handles) {
  localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(Array.from(new Set(handles))));
}

function formatMoney(cents) {
  const amount = (Number(cents) || 0) / 100;
  const currency = window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

async function fetchProductByHandle(handle) {
  const response = await fetch(`/products/${handle}.js`);
  if (!response.ok) return null;
  return response.json();
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str == null ? '' : String(str);
  return div.innerHTML;
}

/** Short label for title line, e.g. "Medium / Pack of 2" → "Medium" */
function variantShortLabel(variantTitle) {
  if (!variantTitle || variantTitle === 'Default Title') return '';
  const first = variantTitle.split(/\s*\/\s*/)[0].trim();
  return first || variantTitle;
}

function titleSuffixForVariant(variantTitle) {
  const short = variantShortLabel(variantTitle);
  return short ? ` - ${short}` : '';
}

/** Select option label: pack only (no size). "Medium / Pack of 2" → "Pack of 2" */
function variantPackLabelForDropdown(variantTitle) {
  if (!variantTitle || variantTitle === 'Default Title') return 'Standard';
  const parts = variantTitle
    .split(/\s*\/\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) return parts.slice(1).join(' / ');
  return variantTitle;
}

function variantImageSrc(variant) {
  if (!variant || !variant.featured_image) return '';
  const fi = variant.featured_image;
  return typeof fi === 'string' ? fi : fi.src || '';
}

function productImageUrl(product) {
  if (!product) return '';
  const fi = product.featured_image;
  if (typeof fi === 'string') return fi;
  if (fi && typeof fi === 'object' && fi.src) return fi.src;
  const img0 = Array.isArray(product.images) ? product.images[0] : null;
  if (typeof img0 === 'string') return img0;
  if (img0 && typeof img0 === 'object' && img0.src) return img0.src;
  return '';
}

function renderCard(product) {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const firstVariant = variants[0];
  let selectedIdx = 0;
  if (variants.length) {
    const firstAvail = variants.findIndex((v) => v.available !== false);
    if (firstAvail >= 0) selectedIdx = firstAvail;
  }
  const selectedVariant = variants[selectedIdx] || firstVariant;
  const ratingCount = product.metafields?.reviews?.rating_count || 2;
  const titleSafe = escapeHtml(product.title);
  const card = document.createElement('article');
  card.className = 'wishlist-item';
  card.dataset.handle = product.handle;
  const imgAlt = escapeHtml(product.title);
  const showVariantPicker =
    variants.length > 1 || (firstVariant && firstVariant.title && firstVariant.title !== 'Default Title');
  const defaultProductImage = productImageUrl(product);
  const initialImage =
    (selectedVariant && variantImageSrc(selectedVariant)) || defaultProductImage || '';
  const initialPrice = selectedVariant ? selectedVariant.price : product.price;
  const initialSuffix = selectedVariant ? escapeHtml(titleSuffixForVariant(selectedVariant.title)) : '';

  card.dataset.wishlistDefaultImg = defaultProductImage || '';

  card.innerHTML = `
    <div class="wishlist-item__card">
      <div class="wishlist-item__media-wrap">
        <button type="button" class="wishlist-item__heart" data-remove-handle="${product.handle}" aria-label="Remove from wishlist">
          <span class="wishlist-item__wishlist-icon">${WISHLIST_HEART_SVG}</span>
        </button>
        <a class="wishlist-item__gallery" href="${product.url}">
          ${initialImage ? `<img src="${initialImage}" alt="${imgAlt}" width="300" height="300" loading="lazy" data-wishlist-product-img>` : ''}
        </a>
      </div>
      <div class="wishlist-item__content">
        <h3 class="wishlist-item__title">
          <a class="wishlist-item__title-link" href="${product.url}">
            <span class="wishlist-item__title-base">${titleSafe}</span><span class="wishlist-item__title-suffix" data-wishlist-title-suffix>${initialSuffix}</span>
          </a>
        </h3>
        <p class="wishlist-item__rating">★★★★☆ <span class="wishlist-item__rating-count">(${ratingCount})</span></p>
        <div class="wishlist-item__price-row">
          <p class="wishlist-item__price" data-wishlist-price>${formatMoney(initialPrice)}</p>
          ${
            showVariantPicker
              ? `<select class="wishlist-item__variant-select" data-wishlist-variant-select aria-label="Choose product option"></select>`
              : ''
          }
        </div>
        <div class="wishlist-item__actions">
          <a href="${product.url}" class="wishlist-item__cta"><span class="wishlist-item__cta-text">Add to Cart</span></a>
          <button type="button" class="wishlist-item__remove" data-remove-handle="${product.handle}">Remove</button>
        </div>
      </div>
    </div>
  `;

  if (showVariantPicker) {
    const select = card.querySelector('[data-wishlist-variant-select]');
    if (select instanceof HTMLSelectElement) {
      variants.forEach((v, i) => {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        const fullTitle = v.title || 'Option';
        opt.dataset.variantFullTitle = fullTitle;
        opt.textContent = variantPackLabelForDropdown(fullTitle);
        opt.dataset.price = String(v.price ?? 0);
        const vSrc = variantImageSrc(v);
        if (vSrc) opt.dataset.imageSrc = vSrc;
        if (v.available === false) {
          opt.disabled = true;
        }
        opt.selected = i === selectedIdx;
        select.appendChild(opt);
      });
      applyWishlistVariantFromSelect(select);
    }
  }

  return card;
}

function applyWishlistVariantFromSelect(select) {
  if (!(select instanceof HTMLSelectElement)) return;
  const item = select.closest('.wishlist-item');
  if (!(item instanceof HTMLElement)) return;
  const opt = select.selectedOptions[0];
  if (!opt) return;
  const priceEl = item.querySelector('[data-wishlist-price]');
  const suffixEl = item.querySelector('[data-wishlist-title-suffix]');
  const img = item.querySelector('[data-wishlist-product-img]');
  const price = Number(opt.dataset.price || 0);
  if (priceEl) priceEl.textContent = formatMoney(price);
  const fullTitle = opt.dataset.variantFullTitle || opt.textContent || '';
  if (suffixEl) {
    suffixEl.textContent = titleSuffixForVariant(fullTitle);
  }
  if (img instanceof HTMLImageElement) {
    if (opt.dataset.imageSrc) {
      img.src = opt.dataset.imageSrc;
    } else {
      const fallback = item.dataset.wishlistDefaultImg;
      if (fallback) img.src = fallback;
    }
  }
}

async function renderWishlistPage() {
  const grid = document.querySelector('[data-wishlist-grid]');
  const emptyState = document.querySelector('[data-wishlist-empty]');
  const subtitle = document.querySelector('[data-wishlist-subtitle]');
  if (!(grid instanceof HTMLElement) || !(emptyState instanceof HTMLElement)) return;

  const handles = getWishlistHandles();
  if (handles.length === 0) {
    grid.innerHTML = '';
    emptyState.hidden = false;
    if (subtitle instanceof HTMLElement) subtitle.textContent = 'Your liked products appear here.';
    return;
  }

  const products = await Promise.all(handles.map((handle) => fetchProductByHandle(handle)));
  const validProducts = products.filter(Boolean);

  if (validProducts.length === 0) {
    grid.innerHTML = '';
    emptyState.hidden = false;
    if (subtitle instanceof HTMLElement) subtitle.textContent = 'Your liked products appear here.';
    return;
  }

  emptyState.hidden = true;
  if (subtitle instanceof HTMLElement) {
    subtitle.textContent = `${validProducts.length} product${validProducts.length > 1 ? 's' : ''} saved.`;
  }

  grid.innerHTML = '';
  validProducts.forEach((product) => grid.appendChild(renderCard(product)));
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (target instanceof HTMLSelectElement && target.matches('[data-wishlist-variant-select]')) {
    applyWishlistVariantFromSelect(target);
  }
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const removeButton = target.closest('[data-remove-handle]');
  if (removeButton instanceof HTMLElement) {
    const handle = removeButton.dataset.removeHandle;
    if (!handle) return;

    const nextHandles = getWishlistHandles().filter((item) => item !== handle);
    setWishlistHandles(nextHandles);
    renderWishlistPage();
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { count: nextHandles.length } }));
    return;
  }

  const addButton = target.closest('.wishlist-item__cta');
  if (!(addButton instanceof HTMLAnchorElement)) return;

  event.preventDefault();

  const item = addButton.closest('.wishlist-item');
  const select = item instanceof HTMLElement ? item.querySelector('[data-wishlist-variant-select]') : null;
  const selectedVariantId =
    select instanceof HTMLSelectElement && select.value ? select.value : '';

  const destination = new URL(addButton.href, window.location.origin);
  if (selectedVariantId) {
    destination.searchParams.set('variant', selectedVariantId);
  }

  window.location.href = destination.toString();
});

window.addEventListener('storage', (event) => {
  if (event.key === WISHLIST_STORAGE_KEY) {
    renderWishlistPage();
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderWishlistPage);
} else {
  renderWishlistPage();
}
